<template>
  <button
    class="c-bitts-filter-button"
    :class="buttonClasses"
    type="button"
    :disabled="disabled"
    :aria-expanded="isOpen ? 'true' : 'false'"
    @click="handleClick"
  >
    <!-- PREFIX -->
    <slot
      name="prefix"
      :active="active"
      :value-text="valueText"
      :label-text="labelText"
    >
      <FontAwesomeIcon
        v-if="!hideIcon"
        :icon="prefixIcon"
        :class="[iconColor, iconSizeClass]"
      />
    </slot>

    <!-- LABEL + VALUE -->
    <span v-if="!showIconOnly">
      <span :class="[labelColor, 'font-bold']">{{ labelText }}</span>
      <span :class="[valueColor, 'font-bold']" class="ml-3">{{
        valueText
      }}</span>
    </span>

    <!-- SUFFIX -->
    <slot
      v-if="!showIconOnly || count"
      name="suffix"
      :active="active"
      :value-text="valueText"
      :label-text="labelText"
    >
      <FontAwesomeIcon
        v-if="rightIcon && !count && !isColorFilterActive"
        :icon="rightIcon"
        :class="[
          'suffix-icon',
          'text-neutral-accent',
          iconSizeClass,
          { 'rotate-180': rotated },
        ]"
        style="color: currentColor"
      />

      <BittsCount
        v-if="count && ['default', 'simple'].includes(variant)"
        :count="count"
        :disabled="disabled"
        type="upsell"
      />

      <div
        v-if="isColorFilterActive && variant === 'quick'"
        class="clear-quick-filter"
      >
        <BittsTooltip v-if="!removable" :mount-to-body="true">
          <div
            role="presentation"
            @click.stop
            @mouseenter="emit('entered-non-removable')"
            @mouseleave="emit('left-non-removable')"
          >
            <BittsButton
              aria-label="Clear filter"
              size="x-small"
              variant="ghost"
              :aria-disabled="true"
              :center-icon="['fas', 'xmark']"
              center-icon-classes="text-secondary-text"
            />
          </div>
          <template #title>This filter cannot be removed</template>
        </BittsTooltip>
        <BittsButton
          v-else
          aria-label="Clear filter"
          size="x-small"
          variant="ghost"
          :disabled="disabled"
          :center-icon="['fas', 'xmark']"
          center-icon-classes="text-secondary-text"
          @click.stop="emit('cleared:quick-filter', $event)"
        />
      </div>
    </slot>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import BittsCount from '../BittsCount/BittsCount.vue';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';
import { BittsIcon } from '../types';

type Props = {
  labelText?: string;
  hideIcon?: boolean;
  prefixIcon?: BittsIcon;
  suffixIcon?: BittsIcon;
  active?: boolean;
  disabled?: boolean;
  valueText?: string;
  size?: 'small' | 'medium';
  count?: number;
  isOpen?: boolean;
  variant?: 'default' | 'quick' | 'simple';
  showIconOnly?: boolean;
  removable?: boolean;
  isNegated?: boolean;
};
const {
  disabled = false,
  size = 'medium',
  active = false,
  variant = 'default',
  prefixIcon = ['fak', 'filter'],
  hideIcon = false,
  suffixIcon,
  labelText,
  valueText,
  count,
  isOpen = false,
  showIconOnly = false,
  removable = true,
  isNegated = false,
} = defineProps<Props>();

const emit = defineEmits<{
  click: [e: MouseEvent];
  'cleared:quick-filter': [e: MouseEvent];
  'entered-non-removable': [];
  'left-non-removable': [];
}>();

defineSlots<{
  suffix?(props: {
    active: boolean;
    valueText?: string;
    labelText?: string;
  }): unknown;
  prefix?(props: {
    active: boolean;
    valueText?: string;
    labelText?: string;
  }): unknown;
}>();

const isColorFilterActive = computed(
  () => ['simple', 'quick'].includes(variant) && active,
);

const iconSizeClass = computed(() =>
  size === 'small' ? 'h-12 w-12' : 'h-16 w-16',
);

const rotated = computed(() => variant === 'quick' && isOpen);

const labelColor = computed(() => {
  if (disabled) return 'text-neutral-text-placeholder';
  if (active) {
    if (variant === 'default') return 'text-secondary-text-strong';
    return isNegated ? 'text-danger-text' : 'text-secondary-accent';
  }
  return 'text-neutral-text';
});

const valueColor = computed(() => {
  if (disabled) return 'text-neutral-text-placeholder';
  return 'text-secondary-text-strong';
});

const iconColor = computed(() => {
  if (disabled) return 'text-neutral-text-placeholder';
  if (isColorFilterActive.value) {
    return isNegated ? 'text-danger-accent' : 'text-secondary-accent';
  }
  return 'text-neutral-accent';
});

const rightIcon = computed(() => {
  if (suffixIcon) return suffixIcon;

  if (variant === 'quick' && !active) {
    return ['fak', 'chevron-down'];
  }

  return null;
});

const buttonClasses = computed(() => ({
  [variant]: true,
  [size]: true,
  'active-with-color': isColorFilterActive.value && !isNegated,
  'active-with-danger': isColorFilterActive.value && isNegated,
  'icon-only': showIconOnly || (!labelText && !valueText),
  disabled,
  active,
  open: isOpen,
}));

const handleClick = (e: MouseEvent) => {
  if (disabled) return;
  emit('click', e);
};
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-filter-button {
  @apply inline-flex flex-nowrap items-center justify-center gap-4 border border-neutral-border-strong h-32 px-10 leading-4 min-w-0 text-nowrap;

  &:not(.small),
  &:not(.simple) {
    @apply text-sm;
  }

  &:not(.small) {
    @apply rounded-8;
  }

  &.simple {
    @apply text-sm;
  }

  &.small {
    @apply text-sm h-24 px-6 rounded-6;

    :deep(.c-bitts-btn-icon) {
      @apply h-16 w-16;
    }
  }

  &.quick.active {
    @apply pr-4;
  }

  &.disabled {
    @apply text-neutral-text-placeholder cursor-not-allowed;
  }

  &.icon-only:not(.small) {
    @apply px-[7px];
  }
  &.icon-only.small {
    @apply px-[5px];
  }

  .c-bitts-filter-button__prefix {
    @apply flex items-center;
  }

  /* When quick filter is in active state, the button is the clickable element... the button gets the disabled state */
  &:not(.active-with-color):not(.active-with-danger):not(.disabled) {
    @apply cursor-pointer;
  }

  /* Note the padding right is overriding the default here. We want less space because the BittsButton ghost button takes up quite a bit of room */
  &.active-with-color {
    @apply cursor-default bg-secondary-background-weak/50 border-secondary-background-weak/50;

    &:hover {
      @apply bg-secondary-background-weak/75 border-secondary-background-weak/75;
    }
    &:active {
      @apply bg-secondary-background-weak border-secondary-background-weak;
    }
  }

  &.active-with-danger {
    @apply cursor-default bg-danger-background-weak/50 border-danger-background-weak/50;

    &:hover {
      @apply bg-danger-background-weak/75 border-danger-background-weak/75;
    }
    &:active {
      @apply bg-danger-background-weak border-danger-background-weak;
    }
  }

  &.active-with-color.disabled,
  &.active-with-danger.disabled {
    @apply bg-neutral-background-weak;
  }

  &:hover:not(.disabled):not(.active-with-color):not(.active-with-danger) {
    @apply bg-neutral-background-weak;
  }

  &:active:not(.disabled):not(.active-with-color):not(.active-with-danger) {
    @apply bg-neutral-background;
  }

  &:focus-visible:not(.disabled),
  &.open:not(.disabled) {
    @apply outline-neutral-border-focus outline-2 border-transparent outline-offset-[-2px];
  }
}
</style>
